import { useState } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { loaders } from 'entities';

import { Tabs, Skeleton, Row, ResponsiveOverlay } from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';

import { InvoiceEvents } from './Events';
import { InvoiceMainData } from './Main';
import { InvoiceFiles } from './Files';
import { InvoiceAction, statusToAction } from './Action';
import { InvoiceBilling } from './Billing';

import { useUser } from 'hooks';

import { InvoiceTab, OutsourceInvoiceWithProject } from './types';

interface OutsourceInvoiceModalProps {
  children?: never;
  invoice: string | null;
  close: () => void;
  onUpdate?: (invoice: OutsourceInvoiceWithProject) => void;
}

const tabComponent: Record<string, typeof InvoiceEvents> = {
  events: InvoiceEvents,
  data: InvoiceMainData,
  action: InvoiceAction,
  billing: InvoiceBilling,
  files: InvoiceFiles,
};

export function OutsourceInvoiceModal({ invoice, close, onUpdate }: OutsourceInvoiceModalProps) {
  const user = useUser();

  const [tab, setTab] = useState<InvoiceTab>('data');

  const { t } = useLang();

  const { error, loading, data, overwrite } = loaders.invoice.findOutsourceInvoice.use({
    defaultValue: {} as OutsourceInvoiceWithProject,

    params: { id: invoice! },

    blockLoad: !invoice,

    onDataChange: onUpdate,
  });

  const showLoading = !data.createdAt || loading || error;

  const tabs = [
    {
      getLabel: (t: TFunction) => t('data'),
      value: 'data',
    },
    {
      getLabel: (t: TFunction) => t('billing'),
      value: 'billing',
      render: !!data.address,
    },
    {
      getLabel: (t: TFunction) => t('files'),
      value: 'files',
      render: !!data.files?.length,
    },
    {
      getLabel: (t: TFunction) => t('events'),
      value: 'events',
    },
    {
      getLabel: (t: TFunction) => t('action'),
      value: 'action',
      render: !!user.team.isInvoiceTeam && (showLoading ? false : !!statusToAction[data.status]),
    },
  ].filter((r) => r.render ?? true);

  const TabComponent = tabComponent[tab];

  const closeModal = (): void => {
    close();
    setTimeout(() => setTab('data'), 500);
  };

  return (
    <ResponsiveOverlay
      close={closeModal}
      isOpen={!!invoice}
      className="h-[min(750px,70vh)] overflow-y-auto"
      width="min(650px, 80vw)"
      title={
        <Row gap="1">
          <h2 className="text-lg font-semibold leading-none tracking-tight">
            {t('outsource-invoice')}
          </h2>

          {!showLoading && (
            <EntityStatusTag
              target="outsourceInvoice"
              status={data.status}
              size="medium"
              className="ml-2"
            />
          )}
        </Row>
      }
    >
      {showLoading ? (
        <Skeleton height={35} quantity={5} />
      ) : (
        <>
          <Tabs
            value={tab}
            onTabChange={setTab as () => void}
            tabs={tabs}
            className="-ml-2 -mt-2"
          />

          {TabComponent && (
            <TabComponent
              invoice={data}
              overwrite={overwrite}
              changeTab={setTab}
              closeModal={closeModal}
            />
          )}
        </>
      )}
    </ResponsiveOverlay>
  );
}
